"use client";

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface MessageType {
  id: string;
  icon: string;
  title: string;
  description: string;
  color: string;
  bgColor: string;
  size: 'large' | 'small';
  image?: string;
  imageAlt?: string;
}

const MessageTypesSection = () => {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);

  const messageTypes: MessageType[] = [
  {
    id: 'type_letters',
    icon: 'DocumentTextIcon',
    title: 'Letters',
    description: 'Write heartfelt letters for birthdays, graduations, or life advice. Your words, their guidance.',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    size: 'large',
    image: "/images/message-video.jpg",
    imageAlt: 'Handwritten letter with fountain pen on vintage paper with soft lighting'
  },
  {
    id: 'type_videos',
    icon: 'VideoCameraIcon',
    title: 'Videos',
    description: 'Record video messages for weddings, life milestones, or simply to share your story.',
    color: 'text-teal-600',
    bgColor: 'bg-teal-100',
    size: 'large',
    image: "/images/message-audio.jpg",
    imageAlt: 'Person recording video message on smartphone with warm home background'
  },
  {
    id: 'type_audio',
    icon: 'MicrophoneIcon',
    title: 'Audio',
    description: 'Voice messages that capture your tone, laughter, and love.',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    size: 'small'
  },
  {
    id: 'type_memorial',
    icon: 'PhotoIcon',
    title: 'Memorial Page',
    description: 'Create a beautiful tribute with photos, your story, and memories.',
    color: 'text-pink-600',
    bgColor: 'bg-pink-100',
    size: 'small'
  },
  {
    id: 'type_photos',
    icon: 'CameraIcon',
    title: 'Photo Albums',
    description: 'Curate photo collections with captions and stories.',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    size: 'small'
  },
  {
    id: 'type_playlist',
    icon: 'MusicalNoteIcon',
    title: 'Playlists',
    description: 'Share your favorite songs and the memories behind them.',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    size: 'small'
  }];


  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            setVisibleCards((prev) => prev.includes(index) ? prev : [...prev, index]);
          }
        });
      },
      { threshold: 0.1 }
    );

    const cardElements = sectionRef.current?.querySelectorAll('.message-card');
    cardElements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="message-types" ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <span className="inline-block px-4 py-2 bg-secondary/10 text-secondary rounded-full text-sm font-semibold mb-6 uppercase tracking-wider">
            Choose Your Format
          </span>
          <h2 className="font-jakarta font-bold text-4xl md:text-5xl text-foreground mb-6 tracking-tight">
            Every Memory, Every Format
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
            From heartfelt letters to video messages, preserve your legacy in the format that feels right.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 auto-rows-min">
          {messageTypes.map((type, index) =>
          <div
            key={type.id}
            data-index={index}
            className={`message-card group ${
            type.size === 'large' ? 'md:col-span-2 md:row-span-2' : 'md:col-span-1'} bg-card rounded-3xl border border-border overflow-hidden hover:shadow-2xl transition-all duration-500 cursor-pointer ${

            visibleCards.includes(index) ?
            'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`
            }
            style={{ transitionDelay: `${index * 100}ms` }}>
            
              {type.image ?
            <div className="relative h-full min-h-[400px]">
                  <AppImage
                src={type.image}
                alt={type.imageAlt || `${type.title} illustration`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <div className={`w-14 h-14 ${type.bgColor} rounded-2xl flex items-center justify-center mb-4`}>
                      <Icon name={type.icon as any} size={28} className={type.color} />
                    </div>
                    <h3 className="font-jakarta font-bold text-2xl text-white mb-3">{type.title}</h3>
                    <p className="text-white/80 leading-relaxed">{type.description}</p>
                  </div>
                </div> :

            <div className="p-8 h-full flex flex-col justify-between min-h-[200px]">
                  <div className={`w-14 h-14 ${type.bgColor} rounded-2xl flex items-center justify-center mb-4`}>
                    <Icon name={type.icon as any} size={28} className={type.color} />
                  </div>
                  <div>
                    <h3 className="font-jakarta font-bold text-xl text-foreground mb-3">{type.title}</h3>
                    <p className="text-muted-foreground leading-relaxed text-sm">{type.description}</p>
                  </div>
                </div>
            }
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default MessageTypesSection;